import type { ReactElement } from 'react';

import type { ToolUseItem } from '../../../contracts/harness.types';
import { isRecord } from '../../../contracts/parse-utils';
import {
  parseReportIssueDraftInput,
  parseReportIssueResultData,
} from '../../../contracts/report-issue.types';
import { ReportIssueResultCard } from './ReportIssueResultCard';

/**
 * Receipt card for a resolved `report_issue` call. Pending approvals render
 * through `ReportIssueApprovalBlock`; this adapter only covers the terminal
 * states. Non-terminal statuses return null so the tool falls through to the
 * generic row.
 */
const readToolData = (item: ToolUseItem): Record<string, unknown> | undefined => {
  const data = item.result_provider_metadata?.data;
  return isRecord(data) ? data : undefined;
};

export const renderReportIssueToolUse = (item: ToolUseItem): ReactElement | null => {
  const { issueType } = parseReportIssueDraftInput(item.input ?? {});

  if (item.status === 'error') {
    return <ReportIssueResultCard issueType={issueType} failed />;
  }
  if (item.status !== 'complete') return null;

  const rawData = readToolData(item);
  const data = parseReportIssueResultData(rawData);
  if (!data) {
    return item.result?.is_error ? (
      <ReportIssueResultCard issueType={issueType} failed />
    ) : null;
  }
  // The result's own issue_type wins; a result without one falls back to the
  // call's input so a safety receipt never reads as a support one.
  return (
    <ReportIssueResultCard
      issueType={typeof rawData?.issue_type === 'string' ? data.issueType : issueType}
      ticketReference={data.ticketReference}
    />
  );
};
